import { CampaignItem, DiscountTarget } from "../model/Campaign";

export const DEFAULT_DISCOUNT_TARGET: DiscountTarget = "RESELLER_MARGIN";
export const DEFAULT_NIPOSOM_SPLIT_PCT = 50;

export interface CampaignItemFormState {
  promoType: CampaignItem["promo_type"];
  discountPct: number | null;
  discountValue: number | null;
  fixedPvp: number | null;
  discountTarget: DiscountTarget;
  niposomSplitPct: number;
}

/**
 * Maps a persisted campaign item back into the modal form state so the edit
 * form starts from exactly what was saved (target/split included for admins).
 */
export function campaignItemToFormState(
  item: CampaignItem,
): CampaignItemFormState {
  return {
    promoType: item.promo_type,
    discountPct: item.discount_percentage ?? null,
    discountValue: item.discount_value ?? null,
    fixedPvp: item.fixed_pvp ?? null,
    discountTarget: item.discount_target ?? DEFAULT_DISCOUNT_TARGET,
    niposomSplitPct: item.niposom_split_pct ?? DEFAULT_NIPOSOM_SPLIT_PCT,
  };
}

export interface CampaignItemRequestBody {
  compound_id: string;
  promo_type: string;
  /** Only sent when editing; lets the preview endpoint inherit the saved target/split. */
  item_id?: number;
  discount_target?: DiscountTarget;
  niposom_split_pct?: number;
  discount_percentage?: number | null;
  discount_value?: number | null;
  fixed_pvp?: number | null;
}

/**
 * Builds the add/update/preview request body. Target and split are admin-only
 * (server rejects them for other roles), so they are omitted for non-admins.
 * Only the field matching the active promo type is included, so switching type
 * clears the others on save.
 */
export function buildCampaignItemRequest(params: {
  compoundId: string;
  editingItemId?: number | null;
  isAdmin: boolean;
  promoType: string;
  discountPct: number | null;
  discountValue: number | null;
  fixedPvp: number | null;
  discountTarget: DiscountTarget;
  niposomSplitPct: number;
}): CampaignItemRequestBody {
  const body: CampaignItemRequestBody = {
    compound_id: params.compoundId,
    promo_type: params.promoType,
  };
  if (params.editingItemId != null) body.item_id = params.editingItemId;
  if (params.isAdmin) {
    body.discount_target = params.discountTarget;
    if (params.discountTarget === "BOTH") {
      body.niposom_split_pct = params.niposomSplitPct;
    }
  }
  if (params.promoType === "DISCOUNT_PERCENTAGE") {
    body.discount_percentage = params.discountPct;
  } else if (params.promoType === "DISCOUNT_VALUE") {
    body.discount_value = params.discountValue;
  } else if (params.promoType === "FIXED_PVP") {
    body.fixed_pvp = params.fixedPvp;
  }
  return body;
}
